<script setup lang="ts">
import 'vue-sonner/style.css'
import 'driver.js/dist/driver.css'
import { Toaster } from '@/components/ui/sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import WorkspaceAssistantSheet from '@/components/assistant/WorkspaceAssistantSheet.vue'
import ClientQuickCreateSheet from '@/components/clients/ClientQuickCreateSheet.vue'
import { useClientQuickCreate } from '@/composables/clients/useClientQuickCreate'
import ProjectQuickCreateSheet from '@/components/projects/creation/ProjectQuickCreateSheet.vue'
import { useProjectCreationDraft } from '@/composables/projects/useProjectCreationDraft'
import { useProjectQuickCreate } from '@/composables/projects/useProjectQuickCreate'
import MobileNavLauncher from '@/components/navigation/MobileNavLauncher.vue'
import WorkspaceCommandMenu from '@/components/navigation/WorkspaceCommandMenu.vue'
import NotificationDrawer from '@/components/notifications/NotificationDrawer.vue'
import PageActionMenu from '@/components/workspace/PageActionMenu.vue'
import WorkspaceDemoDataBanner from '@/components/workspace/WorkspaceDemoDataBanner.vue'
import TaskQuickCreateSheet from '@/components/tasks/TaskQuickCreateSheet.vue'
import TimerControl from '@/components/time/TimerControl.vue'
import TimeEntrySheet from '@/components/time/TimeEntrySheet.vue'
import { useTimeEntryContextOptions } from '@/composables/time/useTimeEntryContextOptions'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Bell, Bot, CircleHelp, ClipboardPlus, FileText, FolderPlus, LogOut, MailCheck, MoonStar, RefreshCw, ShieldAlert, SunMedium, UserPlus, Zap } from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import { setStoredTenantId, setStoredTenantPath } from '~/lib/workspaceSession'
import type { ProjectCreationDraft } from '~/lib/projectCreation'
import type { Task, TaskPriority, Tenant } from '~/types/models'

definePageMeta({
  middleware: 'auth'
});

const route = useRoute();
const sidebarWidth = useCookie<number>('workspace_sidebar_width', {
    default: () => 256,
    maxAge: 60 * 60 * 24 * 365,
});
const authStore = useAuthStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const teamStore = useTeamStore();
const clientStore = useClientStore();
const projectStore = useProjectStore();
const messagesStore = useMessagesStore();
const notificationStore = useNotificationStore();
const timeEntriesStore = useTimeEntriesStore();
const {
    taskOptions: timeTaskOptions,
    projectOptions: timeProjectOptions,
    clientOptions: timeClientOptions,
    ensureTimeEntryContextOptions,
} = useTimeEntryContextOptions();
const { activeItem, navGroups, settingsNavGroups, isItemActive } = useAppNavigation();
const subscription = useWorkspaceSubscription();
const { canMutate } = usePermissions();
const { tenantPath } = useTenantPath();
const { getHostname, isCustomDomain } = useDomainResolver();
const { startWelcomeTour } = useWorkspaceTour();
const auth = useNuxtApp().$auth;
const publicWorkspaceBranding = useState<Partial<Tenant> | null>('public-workspace-branding', () => null);
const isTrialExpiredPromptOpen = ref(false);
const isMobileNavOpen = ref(false);
const {
    isOpen: isCreateTaskOpen,
    initialDraft: taskCreateDraft,
    openTaskQuickCreate,
    closeTaskQuickCreate,
} = useTaskQuickCreate();
const isAssistantOpen = ref(false);
const isNotificationsOpen = ref(false);
const isWorkspaceTimerSheetOpen = ref(false);
const timerContextLoading = ref(false);
const quickActionLoading = ref(false);
const mobilePageMenu = useMobilePageMenu();
const pageHeaderActions = usePageHeaderActions();
const usesStructuredPageActions = computed(() => pageHeaderActions.value.some(item => item.primary || item.section));
const primaryPageHeaderActions = computed(() => (
    usesStructuredPageActions.value
        ? pageHeaderActions.value.filter(item => item.primary)
        : pageHeaderActions.value
));
const secondaryPageHeaderActions = computed(() => (
    usesStructuredPageActions.value
        ? pageHeaderActions.value.filter(item => !item.primary)
        : []
));
const pageHeaderTitle = usePageHeaderTitle();
const pageShellPresentation = usePageShellPresentation();
const creationDraft = useProjectCreationDraft();
const {
    isProjectQuickCreateOpen,
    projectQuickCreateClientId,
    openProjectQuickCreate,
} = useProjectQuickCreate();
const {
    isClientQuickCreateOpen,
    clientQuickCreateLifecycle,
    openClientQuickCreate,
} = useClientQuickCreate();
const emailAutoSyncRunning = ref(false);
const verificationSending = ref(false);
const demoDataRemoving = ref(false);
let emailAutoSyncTimer: ReturnType<typeof setInterval> | null = null;

const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
        maybeAutoSyncEmailInbox(false);
    }
};
const { isDark, toggleColorMode, themeStyle: appShellBrandStyle } = useUserAppearance();

const taskStatusOptions = ['Todo', 'In Progress', 'Blocked', 'Done'];
const taskPriorityOptions: Array<{ value: TaskPriority; label: string; color: string }> = [
    { value: 'low', label: 'Low', color: 'text-blue-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'critical', label: 'Critical', color: 'text-red-600' },
];
const workspaceBrandingFallbackResolved = ref(false);

const workspaceName = computed(() => (
    tenantStore.currentTenant?.name
    || publicWorkspaceBranding.value?.name
    || 'Workspace'
));
const workspaceAccessMode = computed(() => subscription.entitlements.value.accessMode);
const isSubscriptionStatusPage = computed(() => route.path.endsWith('/settings/subscription'));
const emailVerificationRequired = computed(() => Boolean(authStore.user && !authStore.user.emailVerified));
const workspaceMeta = computed(() => {
    if (tenantStore.currentTenant?.customDomain) {
        return tenantStore.currentTenant.customDomain;
    }

    if (publicWorkspaceBranding.value?.customDomain) {
        return publicWorkspaceBranding.value.customDomain;
    }

    const role = tenantStore.currentTenantRole;
    return role ? `${role.charAt(0).toUpperCase()}${role.slice(1)} access` : 'Workspace access';
});
const headerTitle = computed(() => pageHeaderTitle.value || activeItem.value?.title || workspaceName.value);
const canManageSubscription = computed(() => ['owner', 'admin'].includes(tenantStore.currentTenantRole || ''));
const activeDemoData = computed(() => (
    tenantStore.currentTenant?.demoData?.status === 'active'
        ? tenantStore.currentTenant.demoData
        : null
));
const canManageDemoData = computed(() => canMutate.value && ['owner', 'admin'].includes(tenantStore.currentTenantRole || ''));
const tenantUnreadNotifications = computed(() => notificationStore.unreadCountForContext({
    surface: 'tenant',
    tenantId: tenantStore.currentTenantId,
}));
const quickActionItems = computed(() => [
    {
        id: 'task',
        label: 'Add task',
        icon: ClipboardPlus,
        visible: canMutate.value,
        action: () => openCreateTask(),
    },
    {
        id: 'project',
        label: 'Add project',
        icon: FolderPlus,
        visible: canMutate.value,
        action: () => {
            openProjectQuickCreate();
        },
    },
    {
        id: 'client',
        label: 'Add client',
        icon: UserPlus,
        visible: canMutate.value,
        action: () => {
            openClientQuickCreate();
        },
    },
    {
        id: 'quote',
        label: 'Create quote',
        icon: FileText,
        visible: canMutate.value,
        action: () => openCreateQuote(),
    },
    {
        id: 'ai',
        label: 'AI assistant',
        icon: Bot,
        visible: canMutate.value,
        action: () => openAiAssistant(),
    },
].filter((item) => item.visible));

async function logoutWorkspaceAccount() {
    await authStore.logout();
    await navigateTo('/auth/login', { replace: true });
}

const accountMenuItems = computed(() => [
    {
        title: 'Take workspace tour',
        icon: CircleHelp,
        action: () => startWelcomeTour({ force: true }),
    },
    {
        title: 'Log out',
        icon: LogOut,
        action: logoutWorkspaceAccount,
    },
]);
const settingsCommandItems = computed(() => (
    settingsNavGroups.value.flatMap((group) => group.items)
));
const accountDisplayName = computed(() => authStore.user?.displayName || userStore.user?.displayName || 'Workspace account');
const accountEmail = computed(() => authStore.user?.email || userStore.user?.email || '');
const accountAvatarUrl = computed(() => authStore.user?.photoURL || userStore.user?.avatarURL || '');
const accountInitials = computed(() => {
    const source = accountDisplayName.value || accountEmail.value || 'VS';
    return source
        .split(/[ @.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part: string) => part[0]?.toUpperCase() || '')
        .join('') || 'VS';
});

const taskAssigneeOptions = computed(() => (
    teamStore.members.map((member) => ({
        id: member.userId || member.id,
        label: member.displayName || member.email,
    }))
));
const taskProjectOptions = computed(() => (
    projectStore.projects.map((project) => ({
        id: project.id,
        name: project.name,
        clientId: project.clientId,
        stages: project.stages,
    }))
));
const taskClientOptions = computed(() => (
    clientStore.clients.map((client) => ({ id: client.id, name: client.name }))
));
const trialExpiredStorageKey = computed(() => (
    tenantStore.currentTenantId ? `vuey:trial-expired-snooze:${tenantStore.currentTenantId}` : ''
));
const trialExpiredLabel = computed(() => {
    const value = subscription.entitlements.value.trialEndsAt;
    if (!value) return 'recently';

    const parsed = value instanceof Date
        ? value
        : typeof value === 'object' && value !== null && 'toDate' in value
            ? (value as { toDate: () => Date }).toDate()
            : new Date(String(value));

    if (Number.isNaN(parsed.getTime())) return 'recently';

    return parsed.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
});
const emailInboxIntegration = computed(() => userStore.user?.integrations?.emailInbox || null);
const workspaceAllowsMemberEmail = computed(() =>
    tenantStore.currentTenant?.communicationSettings?.assistantMonitoring?.memberEmailInboxesEnabled ?? false,
);
const emailAutoSyncIntervalMinutes = computed(() => Number(emailInboxIntegration.value?.syncIntervalMinutes ?? 15));
const canAutoSyncEmailInbox = computed(() => {
    const integration = emailInboxIntegration.value;
    return Boolean(
        tenantStore.currentTenantId
        && integration?.provider === 'gmail'
        && integration?.status === 'connected'
        && integration?.enabled
        && integration?.monitorClientThreads
        && workspaceAllowsMemberEmail.value
        && canMutate.value
        && emailAutoSyncIntervalMinutes.value > 0,
    );
});

async function resendVerificationEmailNotice() {
    verificationSending.value = true;

    try {
        await authStore.sendVerificationEmail({
            redirectPath: route.fullPath || tenantPath('/account/security'),
            tenantId: tenantStore.currentTenantId,
        });
        toast.success('Verification email sent.');
    } catch (error: any) {
        toast.error(error?.message || 'We could not resend the verification email.');
    } finally {
        verificationSending.value = false;
    }
}

async function removeWorkspaceDemoData() {
    const tenantId = tenantStore.currentTenantId;
    const token = await auth.currentUser?.getIdToken();
    if (!tenantId || !token || demoDataRemoving.value) return;

    demoDataRemoving.value = true;

    try {
        const response = await $fetch<{ removedCount: number }>('/api/tenants/demo-data', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${token}`,
            },
            body: {
                tenantId,
                action: 'remove',
            },
        });

        toast.success(`${response.removedCount} demo record${response.removedCount === 1 ? '' : 's'} removed. Your workspace is ready for real work.`);

        if (import.meta.client) {
            window.location.reload();
        }
    } catch (error: any) {
        toast.error(error?.data?.statusMessage || error?.message || 'Demo data could not be removed.');
        demoDataRemoving.value = false;
    }
}

async function syncWorkspaceBrandingFallback() {
    const hostname = getHostname();
    if (!isCustomDomain(hostname)) {
        publicWorkspaceBranding.value = null;
        workspaceBrandingFallbackResolved.value = true;
        return;
    }

    try {
        const response = await $fetch<{ tenant: Partial<Tenant> | null }>('/api/public/tenant-branding', {
            query: {
                hostname,
            },
        });
        publicWorkspaceBranding.value = response?.tenant || null;
    } catch (error) {
        console.warn('Unable to load workspace branding fallback:', error);
        publicWorkspaceBranding.value = null;
    } finally {
        workspaceBrandingFallbackResolved.value = true;
    }
}

function snoozeTrialExpiredPrompt() {
    if (import.meta.client && trialExpiredStorageKey.value) {
        window.localStorage.setItem(trialExpiredStorageKey.value, String(Date.now() + (24 * 60 * 60 * 1000)));
    }

    isTrialExpiredPromptOpen.value = false;
}

async function handleQuickProjectCreated(projectId: string) {
    const project = projectStore.projects.find((item) => item.id === projectId);
    await navigateTo(tenantPath(`/clients/projects/${project?.slug || projectId}`));
}

async function handleGuidedProjectSetup(draft: ProjectCreationDraft) {
    creationDraft.value = draft;
    await navigateTo(tenantPath('/projects/new'));
}

async function openSubscriptionUpgrade() {
    isTrialExpiredPromptOpen.value = false;
    await navigateTo(tenantPath('/settings/subscription'));
}

function maybeOpenTrialExpiredPrompt() {
    if (!import.meta.client) return;
    if (!tenantStore.currentTenantId) return;
    if (!canManageSubscription.value) return;
    if (!subscription.entitlements.value.isTrialExpired) return;
    if (route.path.endsWith('/settings/subscription')) return;

    const snoozedUntil = trialExpiredStorageKey.value
        ? Number(window.localStorage.getItem(trialExpiredStorageKey.value) || '0')
        : 0;

    if (snoozedUntil > Date.now()) return;
    isTrialExpiredPromptOpen.value = true;
}

function emailAutoSyncStorageKey() {
    const tenantId = tenantStore.currentTenantId || '';
    const userId = authStore.user?.uid || userStore.user?.id || '';
    if (!tenantId || !userId) return '';
    return `blossum:email-auto-sync:${tenantId}:${userId}`;
}

function updateLocalEmailSyncState(stats?: Record<string, any>, error?: string | null) {
    if (!userStore.user) return;

    userStore.user = {
        ...userStore.user,
        integrations: {
            ...(userStore.user.integrations || {}),
            emailInbox: {
                ...(userStore.user.integrations?.emailInbox || {}),
                lastSyncAt: error ? userStore.user.integrations?.emailInbox?.lastSyncAt : new Date(),
                lastSyncSummary: stats ? { ...stats, syncedAt: new Date() } : userStore.user.integrations?.emailInbox?.lastSyncSummary,
                lastSyncError: error ?? null,
            },
        },
        updatedAt: new Date(),
    };
}

async function maybeAutoSyncEmailInbox(force = false) {
    if (!import.meta.client || !canAutoSyncEmailInbox.value || emailAutoSyncRunning.value) return;
    if (document.visibilityState === 'hidden') return;

    const storageKey = emailAutoSyncStorageKey();
    const intervalMs = emailAutoSyncIntervalMinutes.value * 60 * 1000;
    const lastRunAt = storageKey ? Number(window.localStorage.getItem(storageKey) || '0') : 0;
    if (!force && lastRunAt > 0 && (Date.now() - lastRunAt) < intervalMs) return;

    const token = await auth.currentUser?.getIdToken();
    if (!token || !tenantStore.currentTenantId) return;

    emailAutoSyncRunning.value = true;

    try {
        const response = await $fetch<{ stats?: Record<string, any> }>('/api/google-gmail/sync', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${token}`,
            },
            body: {
                tenantId: tenantStore.currentTenantId,
            },
        });

        if (storageKey) {
            window.localStorage.setItem(storageKey, String(Date.now()));
        }

        updateLocalEmailSyncState(response.stats || undefined, null);

        if (Number(response.stats?.imported || 0) > 0) {
            await messagesStore.fetchThreadSummaries().catch(() => []);
        }
    } catch (error: any) {
        updateLocalEmailSyncState(undefined, error?.data?.statusMessage || error?.message || 'Auto-sync failed.');
    } finally {
        emailAutoSyncRunning.value = false;
    }
}

function resetEmailAutoSyncTimer() {
    if (!import.meta.client) return;

    if (emailAutoSyncTimer) {
        clearInterval(emailAutoSyncTimer);
        emailAutoSyncTimer = null;
    }

    if (!canAutoSyncEmailInbox.value) return;

    maybeAutoSyncEmailInbox(false);
    emailAutoSyncTimer = setInterval(() => {
        maybeAutoSyncEmailInbox(false);
    }, 60 * 1000);
}

async function ensureQuickActionData() {
    if (!tenantStore.currentTenantId) return;

    quickActionLoading.value = true;

    try {
        await Promise.all([
            teamStore.members.length === 0 ? teamStore.fetchTeamMembers(tenantStore.currentTenantId) : Promise.resolve(),
            clientStore.clients.length === 0 ? clientStore.fetchClients() : Promise.resolve(),
            projectStore.projects.length === 0 ? projectStore.fetchProjects() : Promise.resolve(),
        ]);
    } finally {
        quickActionLoading.value = false;
    }
}

function currentMemberAssignee() {
    const keys = new Set(
        [
            authStore.user?.uid,
            authStore.user?.email,
            authStore.user?.displayName,
            userStore.user?.id,
            userStore.user?.email,
            userStore.user?.displayName,
            userStore.user?.fullName,
        ]
            .filter(Boolean)
            .map((value) => String(value).trim().toLowerCase()),
    );

    const member = teamStore.members.find((entry) => {
        const memberKeys = [entry.id, entry.userId, entry.email, entry.displayName]
            .filter(Boolean)
            .map((value) => String(value).trim().toLowerCase());
        return memberKeys.some((value) => keys.has(value));
    });

    if (!member) return null;

    return {
        assigneeId: member.userId || member.id,
        assignee: member.displayName || member.email || '',
    };
}

async function openCreateTask() {
    if (!canMutate.value) return;
    await ensureQuickActionData();
    const assignee = currentMemberAssignee();
    openTaskQuickCreate({
        status: 'Todo',
        priority: 'medium',
        clientVisible: false,
        ...(assignee || {}),
    });
}

async function openCreateQuote() {
    await navigateTo(tenantPath('/quotes/new'));
}

function openAiAssistant() {
    isAssistantOpen.value = true;
}

function handleTaskCreated() {
    closeTaskQuickCreate();
}

function handleTaskDialogOpenChange(value: boolean) {
    if (!value) closeTaskQuickCreate();
}

async function handleClientCreated(clientId: string) {
    const client = clientStore.clients.find((entry) => entry.id === clientId) || await clientStore.fetchClientById(clientId);
    if (client?.slug) await navigateTo(tenantPath(`/clients/${client.slug}`));
}

await syncWorkspaceBrandingFallback();

onMounted(async () => {
    const currentUser = auth.currentUser;

    if (currentUser?.uid) {
        try {
            await userStore.fetchUser(currentUser.uid);
            await tenantStore.fetchUserTenants(currentUser.uid);
        } catch (error) {
            console.error('Failed to initialize workspace context:', error);
        }
    }

    if (import.meta.client) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    notificationStore.startRealtime();
});

async function handleToggleColorMode() {
    try {
        await toggleColorMode();
    } catch (error: any) {
        toast.error(error?.message || 'Unable to save your appearance preference.');
    }
}

watch(
    () => [tenantStore.currentTenantId, tenantStore.currentTenantRole, subscription.entitlements.value.isTrialExpired, route.path],
    () => {
        maybeOpenTrialExpiredPrompt();
        isMobileNavOpen.value = false;

        if (tenantStore.currentTenantId) {
            setStoredTenantId(tenantStore.currentTenantId);
        }

        if (route.path.startsWith('/')) {
            setStoredTenantPath(route.fullPath || route.path);
        }
    },
    { immediate: true },
);

watch(
    () => tenantStore.currentTenantId,
    (tenantId) => {
        if (tenantId) {
            void timeEntriesStore.fetchRunning().catch((error) => {
                console.warn('Unable to restore the active timer:', error);
            });
        }
    },
    { immediate: true },
);

async function stopWorkspaceTimer(entryId: string) {
    try {
        await timeEntriesStore.stopTimer(entryId);
        toast.success('Time entry saved.');
    } catch (error: any) {
        toast.error(error?.message || 'Unable to stop the timer.');
    }
}

async function pauseWorkspaceTimer(entryId: string) {
    try {
        await timeEntriesStore.pauseTimer(entryId);
        toast.success('Timer paused.');
    } catch (error: any) {
        toast.error(error?.message || 'Unable to pause the timer.');
    }
}

async function resumeWorkspaceTimer(entryId: string) {
    try {
        await timeEntriesStore.resumeTimer(entryId);
        toast.success('Timer resumed.');
    } catch (error: any) {
        toast.error(error?.message || 'Unable to resume the timer.');
    }
}

async function openWorkspaceTimerSheet() {
    isWorkspaceTimerSheetOpen.value = true;
    timerContextLoading.value = true;
    try {
        await ensureTimeEntryContextOptions();
    } catch (error: any) {
        toast.error(error?.message || 'Unable to load timer options.');
    } finally {
        timerContextLoading.value = false;
    }
}

async function startWorkspaceTimer(value: any = {}) {
    try {
        await timeEntriesStore.startTimer(value);
        isWorkspaceTimerSheetOpen.value = false;
        toast.success('Timer started.');
    } catch (error: any) {
        toast.error(error?.message || 'Unable to start the timer.');
    }
}

async function startInstantWorkspaceTimer() {
    await startWorkspaceTimer({ description: 'Workspace time' });
}

watch(
    () => [getHostname(), route.path],
    () => {
        void syncWorkspaceBrandingFallback();
    },
);

onBeforeUnmount(() => {
    if (import.meta.client) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    }

    if (emailAutoSyncTimer) {
        clearInterval(emailAutoSyncTimer);
        emailAutoSyncTimer = null;
    }
});

watch(
    () => [
        tenantStore.currentTenantId,
        workspaceAllowsMemberEmail.value,
        emailInboxIntegration.value?.provider,
        emailInboxIntegration.value?.status,
        emailInboxIntegration.value?.enabled,
        emailInboxIntegration.value?.monitorClientThreads,
        emailInboxIntegration.value?.syncIntervalMinutes,
        workspaceAccessMode.value,
    ],
    () => {
        resetEmailAutoSyncTimer();
    },
    { immediate: true },
);

</script>

<template>
    <Toaster class="vuey-notifications" position="bottom-right" :theme="isDark ? 'dark' : 'light'" expand :duration="7000" :visible-toasts="4" close-button />
    <SidebarProvider v-model:width="sidebarWidth" class="app-theme-shell" :style="appShellBrandStyle">
        <WorkspaceAccessRestriction
            v-if="workspaceAccessMode === 'blocked' && !isSubscriptionStatusPage"
            :workspace-name="workspaceName"
            secondary-action-label="Review account status"
            tertiary-action-label="Log out"
            @secondary-action="openSubscriptionUpgrade"
            @tertiary-action="logoutWorkspaceAccount"
        />
        <Dialog v-model:open="isTrialExpiredPromptOpen">
            <DialogContent class="max-w-xl">
                <DialogHeader class="space-y-3">
                    <div class="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">Trial ended</Badge>
                        <Badge variant="outline">{{ workspaceName }}</Badge>
                    </div>
                    <DialogTitle>Upgrade to keep the workspace fully unlocked</DialogTitle>
                    <DialogDescription class="text-sm leading-6 text-muted-foreground">
                        Your free trial ended on {{ trialExpiredLabel }}. Choose the plan you want to continue on and launch Stripe checkout when you are ready.
                    </DialogDescription>
                </DialogHeader>

                <div class="rounded-2xl border border-border/70 bg-muted/30 px-4 py-4 text-sm text-muted-foreground">
                    Teams can keep working, but premium access should now move onto a paid plan instead of staying hidden behind the subscription settings page.
                </div>

                <DialogFooter class="gap-2">
                    <Button variant="outline" @click="snoozeTrialExpiredPrompt">
                        Remind me later
                    </Button>
                    <Button @click="openSubscriptionUpgrade">
                        Review plans
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <WorkspaceAssistantSheet v-model:open="isAssistantOpen" />
        <TimeEntrySheet
            v-model:open="isWorkspaceTimerSheetOpen"
            mode="timer"
            :task-options="timeTaskOptions"
            :project-options="timeProjectOptions"
            :client-options="timeClientOptions"
            :loading="timeEntriesStore.saving || timerContextLoading"
            @submit="startWorkspaceTimer"
        />
        <NotificationDrawer
            v-model:open="isNotificationsOpen"
            surface="tenant"
            :tenant-id="tenantStore.currentTenantId"
        />
        <AppSidebar/>
        <SidebarInset class="min-w-0 bg-transparent" :class="{ 'app-shell-immersive': pageShellPresentation === 'immersive' }">
            <Card
                class="app-shell-header flex-row"
                :class="{ 'app-shell-header-immersive': pageShellPresentation === 'immersive' }"
                role="banner"
            >
                <div class="flex min-w-0 flex-1 items-center gap-3">
                    <div class="hidden md:block">
                        <SidebarTrigger />
                    </div>
                    <p class="min-w-0 truncate text-base font-semibold tracking-tight text-foreground">{{ headerTitle }}</p>
                </div>
                <div data-tour="workspace-command-menu-shell" class="flex flex-1 justify-center px-1 md:px-4">
                    <WorkspaceCommandMenu
                        :workspace-name="workspaceName"
                        :nav-groups="navGroups"
                        :account-items="settingsCommandItems"
                        :quick-actions="quickActionItems"
                        :is-item-active="isItemActive"
                    />
                </div>
                <div class="ml-auto flex flex-1 items-center justify-end gap-2">
                    <div class="hidden md:flex">
                        <TimerControl
                            v-if="canMutate"
                            :entry="timeEntriesStore.runningEntry"
                            :loading="timeEntriesStore.saving"
                            allow-instant-start
                            @start="openWorkspaceTimerSheet"
                            @instant-start="startInstantWorkspaceTimer"
                            @pause="pauseWorkspaceTimer"
                            @resume="resumeWorkspaceTimer"
                            @stop="stopWorkspaceTimer"
                        />
                    </div>
                    <div v-if="secondaryPageHeaderActions.length" class="hidden md:block">
                        <PageActionMenu :items="secondaryPageHeaderActions" />
                    </div>
                    <Button
                        v-for="item in primaryPageHeaderActions"
                        :key="item.id"
                        :variant="item.variant || 'default'"
                        class="hidden md:inline-flex"
                        :disabled="item.disabled"
                        @click="item.action"
                    >
                        <component :is="item.icon" v-if="item.icon" class="mr-2 size-4" />
                        {{ item.label }}
                    </Button>
                    <DropdownMenu v-if="quickActionItems.length > 0">
                        <DropdownMenuTrigger as-child>
                            <Button
                                data-tour="workspace-quick-actions"
                                variant="outline"
                                size="icon"
                                class="relative hidden md:inline-flex"
                                :disabled="quickActionLoading"
                            >
                                <Zap class="size-4" />
                                <span class="sr-only">Open quick actions</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="w-52">
                            <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                v-for="item in quickActionItems"
                                :key="item.id"
                                :disabled="quickActionLoading"
                                class="gap-2"
                                @select="item.action"
                            >
                                <component :is="item.icon" class="size-4 text-muted-foreground" />
                                <span>{{ item.label }}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                        data-tour="workspace-notifications"
                        variant="outline"
                        size="icon"
                        class="relative"
                        @click="isNotificationsOpen = true"
                    >
                        <Bell class="size-4" />
                        <span
                            v-if="tenantUnreadNotifications > 0"
                            class="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
                        >
                            {{ tenantUnreadNotifications > 99 ? '99+' : tenantUnreadNotifications }}
                        </span>
                        <span class="sr-only">Open notifications</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        class="relative"
                        @click="handleToggleColorMode"
                    >
                        <SunMedium v-if="isDark" class="size-4" />
                        <MoonStar v-else class="size-4" />
                        <span class="sr-only">Toggle dark mode</span>
                    </Button>
                </div>
            </Card>
            <div
                v-if="emailVerificationRequired"
                class="mx-4 mb-4 rounded-2xl border border-amber-500/25 bg-amber-500/8 px-4 py-4 text-sm text-muted-foreground md:mx-6"
            >
                <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div class="flex items-start gap-3">
                        <MailCheck class="mt-0.5 size-4 text-amber-600" />
                        <div>
                            <p class="font-medium text-foreground">Email verification recommended</p>
                            <p class="mt-1">
                                You can keep working, but please verify your email soon so account recovery and security checks stay available.
                            </p>
                        </div>
                    </div>
                    <div class="flex flex-wrap items-center gap-3">
                        <Button variant="outline" size="sm" :disabled="verificationSending" @click="resendVerificationEmailNotice">
                            <RefreshCw class="mr-2 size-4" :class="{ 'animate-spin': verificationSending }" />
                            {{ verificationSending ? 'Sending…' : 'Resend verification email' }}
                        </Button>
                        <Button size="sm" @click="navigateTo(tenantPath('/account/security'))">
                            Review security settings
                        </Button>
                    </div>
                </div>
            </div>
            <div
                v-if="workspaceAccessMode === 'read_only'"
                class="mx-4 mb-4 border-y border-amber-500/25 bg-amber-500/8 px-4 py-4 text-sm text-muted-foreground md:mx-6"
            >
                <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div class="flex items-start gap-3">
                        <ShieldAlert class="mt-0.5 size-4 shrink-0 text-amber-600" />
                        <div>
                            <p class="font-medium text-foreground">This workspace is temporarily read-only</p>
                            <p class="mt-1">You can review existing work, but creating or changing workspace content is paused. Contact Vuey support if this is unexpected.</p>
                        </div>
                    </div>
                    <Button as-child variant="outline" size="sm" class="shrink-0">
                        <a href="mailto:support@vuey.co.uk">Contact Vuey support</a>
                    </Button>
                </div>
            </div>
            <WorkspaceDemoDataBanner
                v-if="activeDemoData"
                :workspace-name="workspaceName"
                :record-count="activeDemoData.recordCount"
                :can-remove="canManageDemoData"
                :removing="demoDataRemoving"
                @remove="removeWorkspaceDemoData"
            />
            <main
                class="app-shell-main"
                :class="{ 'app-shell-main-immersive': pageShellPresentation === 'immersive' }"
            >
                <slot />
            </main>

            <MobileNavLauncher
                v-model="isMobileNavOpen"
                :workspace-name="workspaceName"
                :workspace-meta="workspaceMeta"
                :nav-groups="navGroups"
                :settings-nav-groups="settingsNavGroups"
                :account-name="accountDisplayName"
                :account-meta="accountEmail"
                :account-avatar-url="accountAvatarUrl"
                :account-initials="accountInitials"
                :account-items="accountMenuItems"
                :page-sections="mobilePageMenu.sections"
                :page-actions="mobilePageMenu.actions"
                :quick-actions="quickActionItems"
                :quick-action-loading="quickActionLoading"
                :is-item-active="isItemActive"
            />
        </SidebarInset>

        <ClientQuickCreateSheet
            v-model:open="isClientQuickCreateOpen"
            :initial-lifecycle-status="clientQuickCreateLifecycle"
            @created="handleClientCreated"
        />

        <ProjectQuickCreateSheet
            v-model:open="isProjectQuickCreateOpen"
            :initial-client-id="projectQuickCreateClientId"
            @created="handleQuickProjectCreated"
            @guided="handleGuidedProjectSetup"
        />

        <TaskQuickCreateSheet
            :open="isCreateTaskOpen"
            :initial-draft="taskCreateDraft"
            :status-options="taskStatusOptions"
            :assignee-options="taskAssigneeOptions"
            :priority-options="taskPriorityOptions"
            :project-options="taskProjectOptions"
            :client-options="taskClientOptions"
            @update:open="handleTaskDialogOpenChange"
            @created="handleTaskCreated"
        />
    </SidebarProvider>
</template>
